// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Grid, Typography } from '@mui/material'

// ** Child Imports
import { WidgetType, UTMType, AttributionType } from 'src/lib/types/dashboardAnalytics'
import { arrayToCsv, downloadCsvHelper, numberWithCommas } from 'src/lib/utils/helpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { clickWiseAttributionOptions, saleByWidgetFilterOptions } from 'src/assets/AnalyticsConfig'
import { extractDateFromFilters, getAnalyticsWidgetPayload } from 'src/lib/utils/analyticsHelpers'
import { analyticsConstants } from 'src/lib/utils/constants'
import { ArrowDownRight, ArrowUpRight, Download, Minus, TrendingDown, TrendingUp } from 'react-feather'
import FloDataGrid from 'src/components/common/data-grid/FloDataGrid'
import { GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro'
import ExportComponent from 'src/components/common/export-component/ExportComponent'
import { ExportOptionConfigType } from 'src/lib/types/exports'
import { addDays, startOfDay } from 'date-fns'
import { ClickWiseAttributionsType, OverviewWidgetFilterOptionsType } from '../../../lib/types/dashboardAnalytics'

const SalesContributorsTable: React.FC<WidgetType> = ({
  value,
  hideDownloadReport = true,
  hideSalesByDropdown = false,
  isLoading,
  error,
  triggerRefresh,
  triggerDownloadReport,
  downloadTriggered,
  hideBorder = false,
  hideRowDivider = false,
  isPaginationEnabled = true,
  hideHeader = false,
  hideFooter = false,
  removeCellPadding = false,
  tableVariant = 'table',
  isReportView = false,
  UTMType = 'source',
  setAttributionType,
  moreIsGood,
  hasComparison
}) => {
  const { analyticsFilters } = useFilter()

  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [selectedUTMType, setSelectedUTMType] = useState<UTMType>('source')
  const [selectedAtrributionType, setSelectedAttributionType] = useState<AttributionType>('LAST_CLICK')
  // const [ hideLanding ]

  const exportOptionData: ExportOptionConfigType[] = [
    {
      valueType: 'orders_utm',
      label: 'Export order wise UTM data'
    }
  ]

  useEffect(() => {
    if (!UTMType) return
    setSelectedUTMType(UTMType)
  }, [UTMType])

  useEffect(() => {
    if (typeof value !== 'undefined') setPage(0)
  }, [value])

  const handlePageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (pageSize: number) => {
    triggerRefresh(
      getAnalyticsWidgetPayload(
        selectedAtrributionType === 'LAST_CLICK'
          ? analyticsConstants.FLO_SALES_WIDGET
          : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
        extractDateFromFilters(analyticsFilters, 'startDate'),
        extractDateFromFilters(analyticsFilters, 'endDate'),
        undefined,
        selectedUTMType as UTMType
      )
    )
    setRowsPerPage(pageSize)
    setPage(0)
  }

  useEffect(() => {
    if (downloadTriggered) {
      downloadCsvHelper(arrayToCsv(value, ['UTM', 'Revenue', 'Order count']), 'utm_report.csv')
      typeof triggerDownloadReport !== 'undefined' && triggerDownloadReport(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadTriggered])

  const columns: GridColumns = [
    {
      field: 'salesBy',
      flex: 2,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['salesBy']
        const sececondayValue = row['ordersCount']

        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{ color: 'text.primary', lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {value === '' ? 'Direct' : value}
            </Typography>
            <Typography variant='body2' sx={{ color: '#949494', lineHeight: '18px' }}>
              {numberWithCommas(sececondayValue, 0)} orders
            </Typography>
          </div>
        )
      }
    },
    {
      field: 'revenue',
      maxWidth: 100,
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['revenue']
        return (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'start',
              margin: 0,
              fontWeight: 500,
              color: '#4D4D4D'
            }}
          >
            <span>{`\u20B9`}</span>
            <span>{numberWithCommas(value)}</span>
          </p>
        )
      }
    },
    {
      field: 'compareToVal',
      maxWidth: hasComparison ? 70 : 0,
      width: hasComparison ? 70 : 0,
      minWidth: hasComparison ? 70 : 0,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['compareToVal']
        const isGood = moreIsGood ? Boolean(parseInt(value) > 0) : Boolean(parseInt(value) < 0)

        return hasComparison ? (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              gap: 2,
              margin: 0,
              fontWeight: 600,
              color: isGood ? '#2C874A' : '#CB2711'
            }}
          >
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              isGood ? (
                <ArrowUpRight className='h-4 w-4 stroke-[2.5px]' />
              ) : (
                <ArrowDownRight className='h-4 w-4 stroke-[2.5px]' />
              )
            ) : (
              <></>
            )}
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              <> {Math.sign(parseInt(value)) === -1 ? value?.slice(1) : value}</>
            ) : (
              <Minus className='text-[#949494] w-[14px] stroke-[2.5px]' />
            )}
          </p>
        ) : (
          <></>
        )
      }
    }
  ]

  return (
    <div className='flex flex-col w-full space-y-4'>
      <Grid item sx={{ width: '100%', position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            width: '50%',
            justifyContent: 'end',
            position: 'absolute',
            top: -80,
            right: 0
          }}
        >
          {!Boolean(hideDownloadReport) ? (
            <Button
              variant='outlined'
              sx={{ height: 36 }}
              disabled={!value?.length}
              onClick={() => downloadCsvHelper(arrayToCsv(value, ['UTM', 'Revenue', 'Order count']), 'utm_report.csv')}
            >
              <Download style={{ transform: 'scale(0.75)' }} />
              Download report
            </Button>
          ) : (
            <></>
          )}
        </div>
        <div className='absolute top-[-81px] right-[180px]'>
          {isReportView ? (
            <ExportComponent
              isExportDisabled={false}
              globalEndDate={addDays(new Date(), 1)}
              exportOptionData={exportOptionData}
              globalStartDate={startOfDay(new Date())}
              titleText={'Export order wise UTM data'}
              jobButtonText={'Generate'}
              dialogButtonText={'Export'}
              hideRadioButtons={false}
            />
          ) : (
            <></>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: hideSalesByDropdown ? 'end' : 'start',
            gap: 12,
            ...(Boolean(hideSalesByDropdown) ? { position: 'absolute' } : ''),
            top: -81,
            left: 318
          }}
        >
          {!Boolean(hideSalesByDropdown) ? (
            <DropdownButton
              options={
                selectedAtrributionType === 'FIRST_CLICK'
                  ? saleByWidgetFilterOptions?.filter(
                      (option: OverviewWidgetFilterOptionsType) => option.value !== 'landing_page_url'
                    )
                  : saleByWidgetFilterOptions
              }
              handleClick={(index: number) => {
                if (hasComparison) {
                  triggerRefresh(
                    getAnalyticsWidgetPayload(
                      selectedAtrributionType === 'LAST_CLICK'
                        ? analyticsConstants.FLO_SALES_WIDGET
                        : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                      extractDateFromFilters(analyticsFilters, 'startDate'),
                      extractDateFromFilters(analyticsFilters, 'endDate'),
                      undefined,
                      saleByWidgetFilterOptions[index]?.value as UTMType
                    ),
                    getAnalyticsWidgetPayload(
                      selectedAtrributionType === 'LAST_CLICK'
                        ? analyticsConstants.FLO_SALES_WIDGET
                        : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                      extractDateFromFilters(analyticsFilters, 'compareToStartDate'),
                      extractDateFromFilters(analyticsFilters, 'compareToEndDate'),
                      undefined,
                      saleByWidgetFilterOptions[index]?.value as UTMType
                    )
                  )
                } else {
                  triggerRefresh(
                    getAnalyticsWidgetPayload(
                      selectedAtrributionType === 'LAST_CLICK'
                        ? analyticsConstants.FLO_SALES_WIDGET
                        : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                      extractDateFromFilters(analyticsFilters, 'startDate'),
                      extractDateFromFilters(analyticsFilters, 'endDate'),
                      undefined,
                      saleByWidgetFilterOptions[index]?.value as UTMType
                    )
                  )
                }
                setSelectedUTMType(saleByWidgetFilterOptions[index]?.value as UTMType)
              }}
              variant='outlined'
            />
          ) : (
            <></>
          )}
          <DropdownButton
            options={
              selectedUTMType === 'landing_page_url'
                ? clickWiseAttributionOptions.filter(
                    (option: ClickWiseAttributionsType) => option.value === 'LAST_CLICK'
                  )
                : clickWiseAttributionOptions
            }
            handleClick={(index: number) => {
              triggerRefresh(
                getAnalyticsWidgetPayload(
                  clickWiseAttributionOptions[index]?.value === 'LAST_CLICK'
                    ? analyticsConstants.FLO_SALES_WIDGET
                    : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                  extractDateFromFilters(analyticsFilters, 'startDate'),
                  extractDateFromFilters(analyticsFilters, 'endDate'),
                  undefined,
                  selectedUTMType
                )
              )
              typeof setAttributionType !== 'undefined' && setAttributionType(clickWiseAttributionOptions[index]?.value)
              setSelectedAttributionType(clickWiseAttributionOptions[index]?.value)
            }}
            variant='outlined'
            size={Boolean(hideSalesByDropdown) ? 'large' : 'small'}
          />
        </div>
      </Grid>
      <FloDataGrid
        page={page}
        changePage={handlePageChange}
        rowsPerPage={rowsPerPage}
        rowHeight={48}
        headerHeight={48}
        totalRowCount={value?.length ?? 0}
        rows={
          isReportView
            ? [...value?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)]
            : [...value?.slice(page * 5, page * 5 + 5)]
        }
        loading={isLoading}
        columns={columns}
        variant={tableVariant}
        hideBorder={hideBorder}
        hideRowDivider={hideRowDivider}
        pagination={isPaginationEnabled}
        hideHeader={hideHeader}
        hideFooter={hideFooter}
        rowsPerPageOptions={[10, 25, 50]}
        changeRowsPerPage={handleChangeRowsPerPage}
        pageSizeOptionsForDropdown={[
          {
            value: 10,
            label: '10'
          },
          {
            value: 25,
            label: '25'
          },
          {
            value: 50,
            label: '50'
          }
        ]}
        removeCellPadding={removeCellPadding}
        noDataJXSElement={
          <Typography variant='subtitle2' sx={{ mt: 2 }}>
            <span style={{ fontWeight: 500, color: 'text.header' }}>No data available.</span>
          </Typography>
        }
      />
    </div>
  )
}

export default SalesContributorsTable
